import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps, Link } from 'react-router-dom';
import { Globals, Constants, Colors, Print, checkPermission } from 'utils';
import { Table, Pagination, Tooltip, Input, DatePicker, Select, Modal, Checkbox } from 'components';
import { BackIcon, CopyIcon, CheckWhiteIcon } from 'assets/icons';
import { MembershipService, PackageService } from 'services';
import { Logo, StripeImage } from 'assets/img';
import moment from 'moment';
import axios from 'axios';
import { Membership, PackagePlan } from 'types';
import MembershipChangeWarningModal from './membership-change-warning-modal';

interface Props extends RootState, RouteComponentProps {
  dispatch: any;
  membership_id?: any;
  onClose?: any;
  item?: any;
  current_plan_user?: any;
  scrollToPackages?: boolean;
}

const STEPS = {
  MEMBERSHIP: 1,
  TIME: 2,
  PAYMENT_METHOD: 3,
};

const MODES = {
  MONTH: 'month',
  YEAR: 'year',
};

class AddMemberships extends React.Component<Props> {
  formRef = React.createRef<HTMLDivElement>();
  packagesRef = React.createRef<HTMLDivElement>();
  scrolledToPackages = false;

  state = {
    memberships: [],
    packages: [] as PackagePlan[],
    selected_package: null as PackagePlan | null,
    iva: 0,
    conversion: 0,
    payment_methods: [],
    step: STEPS.MEMBERSHIP,
    membership: null as Membership | null,
    mode: '',
    payment_method_id: '',
    form: {
      expiration_month: '',
      expiration_year: '',
      number: '',
      cvv: '',
      bank_id: '',
      phonecode_id: '',
      phone: '',
      date: '',
      reference: '',
      sypago_bank_code: '',
      sypago_phone: '',
      sypago_phonecode: '',
      sypago_document_type: '',
      sypago_document: '',
      sypago_otp: '',
      sypago_name: '',
      is_checked: false,
      months: [],
      change_plan: false,
      // Código de referido opcional -- solo aplica a compra de membresía
      // (no paquetes) y solo si el usuario aún no pagó su primera membresía
      // (ver render(): misma condición que Mobile en
      // select-payment-method.tsx). Viaja automáticamente en el payload de
      // MembershipService.pay() via el spread "...this.state.form" ya
      // existente en submit().
      referral_code: '',
    },
    months: [],
    years: [],
    banks: [],
    bank_data: null,
    sypago_banks: [],
    phonecodes: [],
    document_types: [],
    otp_send: false,
    otp_request_id: null as number | null,
    // Aviso de riesgo al cambiar de membresía: estado del modal de
    // confirmación mostrado cuando el usuario ya tiene una membresía
    // activa y selecciona "Adquirir" sobre una membresía distinta.
    show_change_warning: false,
    pending_membership: null as any,
    // Promo temporal (ver applyPromoCode): `promo_active` lo decide el backend (ventana de
    // vigencia) y habilita el campo de código a quien ya compró antes en planes anuales.
    // `quote` guarda la última cotización de POST /quote junto con la clave de los datos con
    // los que se pidió (membresía + modo + código): si esos datos cambian, deja de aplicarse.
    promo_active: false,
    quote: null as { data: any; key: string } | null,
  };

  // Descarta respuestas de cotización que llegan fuera de orden (p. ej. cambios rápidos de
  // método de pago).
  quoteSeq = 0;

  async componentDidMount() {
    this.load();
    this.loadData();

    let months = [];
    let years = [];

    for (let i = 1; i <= 12; i++) {
      months.push({
        value: moment(i, 'M').format('MM'),
        name: moment(i, 'M').format('MM'),
      });
    }

    for (let i = 0; i < 10; i++) {
      years.push({
        value: moment().add(i, 'years').format('YYYY'),
        name: moment().add(i, 'years').format('YYYY'),
      });
    }

    await this.setState({ months, years });
  }

  componentDidUpdate() {
    this.maybeScrollToPackages();
  }

  maybeScrollToPackages = () => {
    if (!this.props.scrollToPackages) return;
    if (this.scrolledToPackages) return;
    if (this.state.step !== STEPS.MEMBERSHIP) return;
    if (this.state.packages.length === 0) return;

    const target = this.packagesRef.current;
    if (!target) return;

    this.scrolledToPackages = true;

    const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
      let node = el?.parentElement || null;
      while (node) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const canScroll = overflowY === 'auto' || overflowY === 'scroll';
        if (canScroll && node.scrollHeight > node.clientHeight) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    };

    const scrollParent = getScrollParent(target);

    if (scrollParent) {
      const maxScrollTop = scrollParent.scrollHeight - scrollParent.clientHeight;
      scrollParent.scrollTo({ top: maxScrollTop, behavior: 'smooth' });
    } else {
      const maxScrollTop = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo({ top: maxScrollTop, behavior: 'smooth' });
    }
  };

  loadData = async () => {
    const res: any = await MembershipService.getBanks({ withoutLoading: true });
    this.setState({
      banks: res.banks || [],
      bank_data: res.bank_data,
      sypago_banks: res.sypago_banks || [],
      document_types: res.document_types || [],
      phonecodes: res.phonecodes || [],
      form: {
        ...this.state.form,
        sypago_document_type: res.document_types.length > 0 ? res.document_types[0].name : '',
        sypago_phonecode: res.phonecodes.length > 0 ? res.phonecodes[0].name : '',
        phonecode_id: res.phonecodes.length > 0 ? res.phonecodes[0].id : '',
      },
    });
  };

  load = async () => {
    const [res, packagesRes]: any[] = await Promise.all([
      MembershipService.get({ user_id: this.props.user?.id }),
      PackageService.get({ user_id: this.props.user?.id, withoutLoading: true }),
    ]);
    this.setState(
      {
        memberships: res.memberships || [],
        packages: packagesRes.packages || [],
        iva: res.iva?.value || 0,
        conversion: res.conversion?.amount || 0,
        payment_methods: res.payment_methods || [],
        promo_active: !!res.promo_active,
      },
      () => {
        const membership_id = this.props.membership_id;
        const item = this.props.item;
        if (membership_id) {
          const membership = res.memberships.find((i: any) => i.id == membership_id);
          if (membership) {
            if (item) {
              this.setState(
                {
                  membership,
                  mode: item.mode,
                  form: { ...this.state.form, months: item.months },
                },
                () => this.setStep(STEPS.PAYMENT_METHOD),
              );
              return;
            }
            this.setState({ membership }, () => this.setStep(STEPS.TIME));
          }
        }
      },
    );
  };

  back = (e: any) => {
    e.preventDefault();
    const { step, selected_package } = this.state;
    if (this.props.item) {
      this.props.onClose?.call(this, false, true);
    }
    switch (step) {
      case STEPS.TIME:
        this.setStep(STEPS.MEMBERSHIP);
        break;
      case STEPS.PAYMENT_METHOD:
        // Los paquetes son de precio cerrado: no pasan por STEPS.TIME,
        // así que "volver" desde el método de pago regresa directo
        // a la selección de membresía/paquete.
        if (selected_package) {
          this.setState({ selected_package: null, payment_method_id: '' }, () =>
            this.setStep(STEPS.MEMBERSHIP),
          );
        } else {
          // El descuento aplicado no se arrastra a la pantalla de precios: se vuelve a aplicar
          // (o se recotiza al pagar) con los datos que queden finalmente.
          this.setState({ quote: null }, () => this.setStep(STEPS.TIME));
        }
        break;
    }
  };

  setStep = (step: number) => {
    this.setState({ step });
  };

  /**
   * Se dispara al hacer clic en "Adquirir" sobre una tarjeta de membresía
   * en STEPS.MEMBERSHIP. Si el usuario YA tiene una membresía activa
   * (this.props.current_plan_user viene poblado desde memberships.tsx en
   * ese caso) y la membresía seleccionada es distinta a la actual, se
   * intercepta el flujo y se muestra el modal de aviso de riesgo en lugar
   * de avanzar directo a STEPS.TIME. Si no tiene membresía activa (primera
   * adquisición) o selecciona la misma membresía que ya tiene, el flujo
   * continúa igual que antes, sin aviso.
   */
  handleSelectMembership = (item: any) => {
    const activeMembershipId = this.props.current_plan_user?.membership_id;
    const isSameMembership = activeMembershipId == item.id;
    const hasActiveMembership = !!activeMembershipId && !isSameMembership;

    if (hasActiveMembership) {
      this.setState({
        show_change_warning: true,
        pending_membership: item,
      });
      return;
    }

    this.applyMembershipSelection(item);
  };

  /**
   * Aplica la selección de membresía y avanza a STEPS.TIME. Se usa tanto
   * para la primera adquisición (sin aviso) como para el cambio de
   * membresía una vez que el usuario confirma explícitamente el aviso
   * de riesgo.
   */
  applyMembershipSelection = (item: any) => {
    const hasMonthly = item.price_month != null && Number(item.price_month) > 0;
    const hasYearly = item.price_year != null && Number(item.price_year) > 0;
    const autoMode =
      hasMonthly && !hasYearly ? MODES.MONTH : hasYearly && !hasMonthly ? MODES.YEAR : '';

    this.setState(
      {
        membership: item,
        mode: autoMode,
        form: { ...this.state.form, change_plan: true },
      },
      () => this.setStep(STEPS.TIME),
    );
  };

  /**
   * Confirmación explícita del aviso de riesgo de cambio de membresía.
   * Cierra el modal y avanza al flujo normal de selección de tiempo/pago.
   */
  confirmChangeWarning = () => {
    const { pending_membership } = this.state;
    if (!pending_membership) return;

    this.setState(
      {
        show_change_warning: false,
        pending_membership: null,
      },
      () => this.applyMembershipSelection(pending_membership),
    );
  };

  /**
   * Cancelación del aviso de riesgo: el usuario decide NO continuar con
   * el cambio de membresía. Se cierra el modal y se vuelve a la pantalla
   * de selección de membresías (STEPS.MEMBERSHIP), sin aplicar ningún
   * cambio.
   */
  cancelChangeWarning = () => {
    this.setState({
      show_change_warning: false,
      pending_membership: null,
    });
  };

  handleSelectPackage = (item: PackagePlan) => {
    this.setState(
      {
        selected_package: item,
        membership: null,
        // referral_code no aplica a paquetes -- se limpia explícitamente
        // para que un código tecleado antes de cambiar a un paquete no
        // viaje igual en el payload de PackageService.pay() via el spread
        // "...this.state.form".
        form: { ...this.state.form, change_plan: false, referral_code: '' },
      },
      () => this.setStep(STEPS.PAYMENT_METHOD),
    );
  };

  selectPaymentMethod = (id: any) => {
    if (this.isOwnReferralCode()) {
      Globals.showError('No puedes usar tu propio código de referido');
      return;
    }

    this.setState({ payment_method_id: id }, () => {
      // El IGTF de Stripe depende del método: si hay una promo aplicada, se recotiza.
      this.refreshPromoQuote();
      setTimeout(() => {
        this.formRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 50);
    });
  };

  private openFacturaPdf = (relPath?: string) => {
    if (!relPath) return;
    const base = (Constants.BASE_PUBLIC || '').replace(/\/+$/, '');
    const rel = relPath.startsWith('/') ? relPath : `/${relPath}`;
    const url = `${base}${rel}`;
    if ((window as any).webkit?.messageHandlers?.cordova_iab) {
      const payload = { message: 'open_url', url };
      (window as any).webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(payload));
      return;
    }
    Print(url, true);
  };

  handlePayResponse = (res: any) => {
    const data = res?.data ?? res;
    if (!data?.ok) {
      Globals.showError(data?.message || 'Lo sentimos, no se pudo procesar el pago');
      return;
    }
    // Señal al menú para que recargue los accesos de servicios
    this.props.dispatch({ type: 'SET_PLAN_USER', payload: Date.now() });
    const itemName = this.state.selected_package
      ? this.state.selected_package?.name
      : this.state.membership?.name;
    const itemLabel = this.state.selected_package ? 'el paquete' : 'la membresía';
    Globals.showSuccess(`Usted acaba de adquirir ${itemLabel} ${itemName}`);
    const pdfUrl = data?.facturaPDF?.pdf;
    if (pdfUrl) window.open(pdfUrl, '_blank');
    this.props.onClose(true);
  };

  submit = async () => {
    const { payment_method_id } = this.state;
    const { is_checked } = this.state.form;

    if (!is_checked) {
      Globals.showError('Por favor, declare que el pago proviene de manera lícita');
      return;
    }

    if (this.isOwnReferralCode()) {
      Globals.showError('No puedes usar tu propio código de referido');
      return;
    }

    // Un código escrito pero sin cotizar podría ser una promoción: si lo es, el total cambia y
    // el usuario debe verlo antes de pagar (en Pago Móvil, antes de transferir).
    if (!(await this.ensureCodeQuoted())) return;

    // Confirma al backend que el usuario vio los montos de POST /quote (con descuento).
    const promo_quoted = !!this.getActivePromoQuote();

    switch (payment_method_id.toString()) {
      case Constants.PAYMENT_METHODS.PAYMENT_MOBILE.toString(): {
        const { bank_id, phone, date, phonecode_id } = this.state.form;
        if (!bank_id) {
          Globals.showError('Por favor, ingrese el banco de origen');
          return;
        }
        if (!phonecode_id) {
          Globals.showError('Por favor, ingrese código del teléfono');
          return;
        }
        if (!phone) {
          Globals.showError('Por favor, ingrese el teléfono');
          return;
        }
        if (!date) {
          Globals.showError('Por favor, ingrese la fecha');
          return;
        }
        try {
          const res = this.state.selected_package
            ? await PackageService.pay({
                user_id: this.props.user?.id,
                package_id: this.state.selected_package?.id,
                payment_method_id,
                ...this.state.form,
                date: this.state.form.date && moment(this.state.form.date).format('YYYY-MM-DD'),
              })
            : await MembershipService.pay({
                user_id: this.props.user?.id,
                membership_id: this.state.membership?.id,
                payment_method_id,
                mode: this.state.mode,
                promo_quoted,
                ...this.state.form,
                date: this.state.form.date && moment(this.state.form.date).format('YYYY-MM-DD'),
              });
          this.handlePayResponse(res);
        } catch (e) {
          console.error(e);
          Globals.showError('Lo sentimos, no se pudo procesar el pago');
        }
        break;
      }

      case Constants.PAYMENT_METHODS.STRIPE.toString(): {
        if (!this.state.form.number) {
          Globals.showError('Por favor, ingrese el número de su tarjeta');
          return;
        }
        if (!this.state.form.expiration_month || !this.state.form.expiration_year) {
          Globals.showError('Por favor, ingrese la fecha de vencimiento de su tarjeta');
          return;
        }
        if (!this.state.form.cvv) {
          Globals.showError('Por favor, ingrese el código secreto de su tarjeta');
          return;
        }
        if (
          moment().add(1, 'month').startOf('month') >
          moment(
            `01-${this.state.form.expiration_month}-${this.state.form.expiration_year}`,
            'DD-MM-YYYY',
          )
        ) {
          Globals.showError('Lo sentimos, la fecha de vencimiento no es válida');
          return;
        }
        const params = new URLSearchParams({
          'card[exp_month]': this.state.form.expiration_month,
          'card[exp_year]': this.state.form.expiration_year,
          'card[number]': this.state.form.number,
          'card[cvc]': this.state.form.cvv,
        }).toString();
        try {
          Globals.setLoading();
          const response = await axios.request({
            method: 'POST',
            url: process.env.REACT_APP_STRIPE_URL + 'tokens?' + params,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              Authorization: 'Bearer ' + process.env.REACT_APP_STRIPE_KEY,
            },
          });
          const res = this.state.selected_package
            ? await PackageService.pay({
                user_id: this.props.user?.id,
                package_id: this.state.selected_package?.id,
                payment_method_id,
                date: this.state.form.date && moment(this.state.form.date).format('YYYY-MM-DD'),
                withoutLoading: true,
                token: response?.data?.id,
              })
            : await MembershipService.pay({
                user_id: this.props.user?.id,
                membership_id: this.state.membership?.id,
                payment_method_id,
                mode: this.state.mode,
                date: this.state.form.date && moment(this.state.form.date).format('YYYY-MM-DD'),
                withoutLoading: true,
                token: response?.data?.id,
                months: this.state.form.months,
                change_plan: this.state.form.change_plan,
                referral_code: this.state.form.referral_code || undefined,
                promo_quoted,
              });
          Globals.quitLoading();
          this.handlePayResponse(res);
        } catch (e) {
          console.error(e);
          Globals.quitLoading();
          Globals.showError('Lo sentimos, no se pudo procesar el pago');
        }
        break;
      }

      case Constants.PAYMENT_METHODS.SYPAGO.toString(): {
        const {
          sypago_document,
          sypago_document_type,
          sypago_bank_code,
          sypago_phonecode,
          sypago_phone,
          sypago_otp,
        } = this.state.form;
        if (
          !sypago_document ||
          !sypago_document_type ||
          !sypago_bank_code ||
          !sypago_phonecode ||
          !sypago_phone
        ) {
          Globals.showError('Por favor, ingrese todos los datos para continuar');
          return;
        }
        if (sypago_phone.length < 7) {
          Globals.showError('El campo teléfono debe tener 7 dígitos');
          return;
        }
        const { total } = this.getTotal(this.state.membership);
        if (!this.state.otp_send) {
          try {
            const res: any = await MembershipService.generateOtp({
              user_id: this.props.user?.id,
              ...this.state.form,
              amount: total,
            });
            this.setState({
              otp_send: true,
              otp_request_id: res.otp_request_id,
            });
          } catch (e) {
            console.error(e);
            Globals.showError('Lo sentimos, no se pudo solicitar la clave OTP');
          }
          return;
        }
        if (!sypago_otp) {
          Globals.showError('Por favor, ingrese su clave OTP');
          return;
        }
        try {
          const res = this.state.selected_package
            ? await PackageService.pay({
                user_id: this.props.user?.id,
                package_id: this.state.selected_package?.id,
                payment_method_id,
                otp_request_id: this.state.otp_request_id,
                ...this.state.form,
                date: this.state.form.date && moment(this.state.form.date).format('YYYY-MM-DD'),
              })
            : await MembershipService.pay({
                user_id: this.props.user?.id,
                membership_id: this.state.membership?.id,
                payment_method_id,
                mode: this.state.mode,
                promo_quoted,
                otp_request_id: this.state.otp_request_id,
                ...this.state.form,
                date: this.state.form.date && moment(this.state.form.date).format('YYYY-MM-DD'),
              });
          this.handlePayResponse(res);
        } catch (e) {
          console.error(e);
          Globals.showError('Lo sentimos, no se pudo procesar el pago');
        }
        break;
      }
    }
  };

  getTotal = (membership: Membership | null) => {
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const trunc2 = (n: number) => Math.trunc(n * 100) / 100;

    // Con un código promocional vigente los montos los calcula el backend (POST /quote) con la
    // misma función que usa pay(): lo que se muestra es exactamente lo que se cobra y, en Pago
    // Móvil, el monto en bolívares que el usuario debe transferir.
    const promoQuote = this.getActivePromoQuote();
    if (promoQuote) {
      const subtotal_bs = round2(promoQuote.subtotal * promoQuote.conversion);
      return {
        subtotal: promoQuote.subtotal,
        iva_total: promoQuote.iva_total,
        igtf_total: promoQuote.igtf_total,
        total: promoQuote.total,
        total_bs: promoQuote.total_bs,
        subtotal_bs,
        iva_bs: round2(promoQuote.total_bs - subtotal_bs),
        discount: promoQuote.discount,
      };
    }

    const subtotal_raw = this.state.selected_package
      ? this.state.selected_package?.price || 0
      : this.state.mode == MODES.MONTH
        ? membership?.price_month || 0
        : this.state.mode == MODES.YEAR
          ? membership?.price_year || 0
          : 0;
    const iva_pct = Number(this.state.iva || 0);
    const igtf_pct = Number(Constants.TAXES.IGTF || 0);
    const isStripe =
      this.state.payment_method_id?.toString() === Constants.PAYMENT_METHODS.STRIPE.toString();
    const rate_bcv = Number(this.state.conversion || 0);
    const iva_raw = subtotal_raw * (iva_pct / 100);
    let total_factura_raw = subtotal_raw + iva_raw;
    if (typeof this.props.item?.total === 'number') {
      total_factura_raw = this.props.item.total;
    }
    const total_factura_usd = round2(total_factura_raw);
    const igtf_raw = isStripe ? total_factura_usd * (igtf_pct / 100) : 0;
    const igtf_usd_display = isStripe ? trunc2(igtf_raw) : 0;
    // Desglose en bolívares (solo relevante para Pago Móvil/SyPago, VEF): el IVA en Bs
    // se DERIVA de (total_bs - subtotal_bs) en vez de recalcularse independiente, para
    // que siempre cuadre exactamente con `total_bs` -- que es el mismo valor que se usa
    // para cobrar -- y no se pierda precisión por doble redondeo en montos chicos (mismo
    // criterio aplicado en la app mobile).
    const subtotal_bs = round2(subtotal_raw * rate_bcv);
    const total_bs = round2((total_factura_raw + igtf_raw) * rate_bcv);
    const iva_bs = round2(total_bs - subtotal_bs);
    return {
      subtotal: round2(subtotal_raw),
      iva_total: round2(iva_raw),
      igtf_total: igtf_usd_display,
      total: round2(total_factura_raw + igtf_raw),
      total_bs,
      subtotal_bs,
      iva_bs,
      discount: 0,
    };
  };

  // Clave de los datos con los que se cotizó: si el usuario cambia membresía, modo o código,
  // la cotización deja de aplicarse sola.
  getQuoteKey = () =>
    [
      this.state.membership?.id,
      this.state.mode,
      (this.state.form.referral_code || '').trim().toUpperCase(),
    ].join('|');

  // Cotización con descuento vigente para los datos actuales, o null. Solo compras de
  // membresía: los paquetes son de precio cerrado y no llevan código.
  getActivePromoQuote = () => {
    const { quote, selected_package } = this.state;
    if (selected_package || !quote || !quote.data?.promo) return null;
    return quote.key === this.getQuoteKey() ? quote.data : null;
  };

  // POST /quote del backend: devuelve los montos exactos que pay() va a cobrar y, si el código
  // es una promoción vigente, con el descuento aplicado. Un código que no es promoción (p. ej.
  // de referido) vuelve con `promo: null` y se sigue validando al pagar, como siempre. Los
  // errores (código vencido, solo planes anuales, etc.) los muestra Api con el mensaje del
  // servidor.
  fetchQuote = async (silent = false): Promise<any> => {
    const { membership, mode, payment_method_id, form } = this.state;
    if (!membership || !mode) return null;
    return MembershipService.quote({
      membership_id: membership.id,
      mode,
      months: form.months,
      payment_method_id: payment_method_id || undefined,
      referral_code: (form.referral_code || '').trim() || undefined,
      withoutLoading: silent,
    });
  };

  // Botón "Aplicar" del campo de código.
  applyPromoCode = async () => {
    if (!(this.state.form.referral_code || '').trim()) {
      Globals.showError('Por favor, ingrese un código');
      return;
    }
    if (this.isOwnReferralCode()) {
      Globals.showError('No puedes usar tu propio código de referido');
      return;
    }
    const seq = ++this.quoteSeq;
    const key = this.getQuoteKey();
    try {
      const data = await this.fetchQuote();
      if (seq !== this.quoteSeq) return;
      this.setState({ quote: data ? { data, key } : null });
    } catch (e) {
      if (seq === this.quoteSeq) this.setState({ quote: null });
    }
  };

  // El IGTF de Stripe depende del método de pago: si ya hay una promo aplicada, se recotiza en
  // silencio al cambiar de método.
  refreshPromoQuote = async () => {
    if (!this.getActivePromoQuote()) return;
    const seq = ++this.quoteSeq;
    const key = this.getQuoteKey();
    try {
      const data = await this.fetchQuote(true);
      if (seq !== this.quoteSeq) return;
      this.setState({ quote: data ? { data, key } : null });
    } catch (e) {
      if (seq === this.quoteSeq) this.setState({ quote: null });
    }
  };

  // Antes de pagar: si hay un código escrito que no se cotizó (el usuario no presionó "Aplicar"),
  // se cotiza ahora. Si resulta ser una promoción el total cambia, así que se corta el pago para
  // que el usuario vea el nuevo monto (en Pago Móvil, antes de transferir). Devuelve true si se
  // puede continuar con el pago.
  ensureCodeQuoted = async (): Promise<boolean> => {
    const code = (this.state.form.referral_code || '').trim();
    if (!code || this.state.selected_package) return true;
    if (this.state.quote?.key === this.getQuoteKey()) return true;

    const seq = ++this.quoteSeq;
    const key = this.getQuoteKey();
    try {
      const data = await this.fetchQuote();
      if (seq !== this.quoteSeq) return false;
      this.setState({ quote: data ? { data, key } : null });
      if (data?.promo) {
        Globals.showWarning(
          `Se aplicó el código ${data.promo.code} y el total a pagar cambió. Revise el nuevo monto y presione Pagar nuevamente.`,
        );
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  // El campo de código lo ve quien aún no pagó su primera membresía (código de referido) y,
  // mientras el backend reporte una promoción vigente (`promo_active`), cualquiera que elija un
  // plan anual.
  canShowCodeField = () => {
    if (this.state.selected_package) return false;
    if (!this.props.user?.first_membership_paid_at) return true;
    return this.state.promo_active && this.state.mode === MODES.YEAR;
  };

  setMode = (mode: number | string) => {
    this.setState({ mode });
  };

  change = (e: any, restart_otp = true) => {
    this.setState({
      form: { ...this.state.form, [e.target.name]: e.target.value },
      otp_send: !restart_otp,
      otp_request_id: restart_otp ? null : this.state.otp_request_id,
    });
  };

  // Handler dedicado (no reusa this.change) para no resetear el estado del
  // OTP de SyPago en cada tecleo del código de referido -- son campos sin
  // relación entre sí.
  changeReferralCode = (e: any) => {
    const value = (e.target.value || '').toUpperCase();
    this.setState({ form: { ...this.state.form, referral_code: value } });
  };

  isOwnReferralCode = (): boolean => {
    const typed = (this.state.form.referral_code || '').trim().toUpperCase();
    const own = (this.props.user?.referral_code || '').trim().toUpperCase();
    return !!typed && !!own && typed === own;
  };

  clipboard = (e: any, text: string) => {
    e.preventDefault();
    navigator.clipboard.writeText(text);
  };

  copyAll = (e: any) => {
    e.preventDefault();
    const { total_bs } = this.getTotal(this.state.membership);
    const bank_data: any = this.state.bank_data;
    const amount = total_bs.toFixed(2);
    const text =
      `${bank_data?.bank?.code ?? ''}\n` +
      `${bank_data?.document_type?.name ?? ''}${bank_data?.document ?? ''}\n` +
      `${bank_data?.phonecode?.name ?? ''}${bank_data?.phone ?? ''}\n` +
      `${amount}`;
    navigator.clipboard.writeText(text);
    Globals.showSuccess('Datos copiados');
  };

  /**
   * Construye las filas de "Servicios contemplados" para una membresía,
   * separando los servicios con cantidad de usos (N o infinito) de los
   * servicios que solo se habilitan con un check (sin cantidad).
   *
   * Caso especial: Revisión documental puede aparecer DOS veces si tiene
   * uses/unlimited Y marketplace=true: una fila con la cantidad de usos
   * ("Revisión documental N") y otra fila con check
   * ("Acceso a revisión documental con abogados externos").
   */
  getServiceRows = (item: any) => {
    const services = item?.membership_services || [];

    const withCount = services.filter(
      (i: any) => i.unlimited || (i.uses != null && Number(i.uses) > 0),
    );

    const withCountIds = new Set(withCount.map((i: any) => i.id));

    const rows: {
      key: string;
      label: string;
      display: 'count' | 'check';
      value?: any;
    }[] = withCount.map((i: any) => ({
      key: `count-${i.id}`,
      label: i.service?.name,
      display: 'count',
      value: i.unlimited ? '∞' : i.uses,
    }));

    const withCheck = services.filter((i: any) => {
      const isWithoutQuantity = !!i.service?.without_quantity;
      const alreadyShownAsCount = withCountIds.has(i.id);
      const isRevisionMarketplace = i.service_id === Constants.SERVICES.REVISION && i.marketplace;

      if (isRevisionMarketplace) return true;
      return isWithoutQuantity && !alreadyShownAsCount;
    });

    withCheck.forEach((i: any) => {
      const label =
        i.service_id === Constants.SERVICES.REVISION
          ? 'Acceso a revisión documental con abogados del directorio'
          : i.service?.name;

      rows.push({
        key: `check-${i.id}`,
        label,
        display: 'check',
      });
    });

    return rows;
  };

  getPackageServiceRows = (item: PackagePlan) => {
    const services = item?.package_services || [];

    const withCount = services.filter(
      (i: any) => i.unlimited || (i.uses != null && Number(i.uses) > 0),
    );

    const withCountIds = new Set(withCount.map((i: any) => i.service_id));

    const rows: {
      key: string;
      label: string | undefined;
      display: 'count' | 'check';
      value?: any;
    }[] = withCount.map((i: any) => ({
      key: `count-${i.service_id}`,
      label: i.service?.name,
      display: 'count',
      value: i.unlimited ? '∞' : i.uses,
    }));

    const withCheck = services.filter((i: any) => {
      const isWithoutQuantity = !!i.service?.without_quantity;
      const alreadyShownAsCount = withCountIds.has(i.service_id);
      return isWithoutQuantity && !alreadyShownAsCount;
    });

    withCheck.forEach((i: any) => {
      rows.push({
        key: `check-${i.service_id}`,
        label: i.service?.name,
        display: 'check',
      });
    });

    return rows;
  };

  render() {
    const { step, payment_method_id } = this.state;
    const membership: Membership | null = this.state.membership;
    const { subtotal, iva_total, igtf_total, total, total_bs, subtotal_bs, iva_bs, discount } =
      this.getTotal(membership);
    const promoQuote = this.getActivePromoQuote();
    const bank_data: any = this.state.bank_data;
    const showBsBreakdown =
      payment_method_id?.toString() === Constants.PAYMENT_METHODS.PAYMENT_MOBILE.toString() ||
      payment_method_id?.toString() === Constants.PAYMENT_METHODS.SYPAGO.toString();

    return (
      <div id='client-add-membership'>
        {/* Aviso de riesgo al cambiar de membresía: solo se muestra cuando
            el usuario ya tiene una membresía activa y selecciona "Adquirir"
            sobre una membresía distinta. Confirmar avanza a STEPS.TIME;
            cancelar regresa a la pantalla de selección de membresías sin
            aplicar cambios. */}
        <MembershipChangeWarningModal
          visible={this.state.show_change_warning}
          currentMembershipName={this.props.current_plan_user?.membership?.name}
          newMembershipName={this.state.pending_membership?.name}
          onConfirm={this.confirmChangeWarning}
          onCancel={this.cancelChangeWarning}
        />

        {step !== STEPS.MEMBERSHIP && (
          <div className='container-back'>
            <Link to='/login' className='back' onClick={(e: any) => this.back(e)}>
              <img src={BackIcon} />
            </Link>
          </div>
        )}

        {step == STEPS.MEMBERSHIP && (
          <>
            <p className='title'>Membresía</p>
            <p className='subtitle'>Seleccione la membresía de acuerdo a su perfil</p>
            <div className='container-memberships'>
              <div className='row'>
                {this.state.memberships.map((item: any) => (
                  <div className='col-md-4' key={item.id}>
                    <div
                      className='item-membership'
                      style={{
                        backgroundColor: item.color?.color || Colors.white,
                      }}
                    >
                      <div className='container-image'>
                        <img src={Constants.BASE_STORAGE + item.image} className='image' />
                        <div style={{ flex: 1 }}>
                          <p className='name'>{item.name}</p>
                          <div className='amount-row'>
                            <p className='amount'>
                              {Globals.formatMiles(
                                (item.month && item.price_month) || (item.year && item.price_year),
                              )}
                            </p>
                            <p className='amount-note'>Precio no incluye IVA</p>
                          </div>
                        </div>
                      </div>
                      <div className='container-white'>
                        <div className='service-item'>
                          <div className='row'>
                            <div className='col-md-7'>
                              <p className='services-title'>Servicios contemplados</p>
                            </div>
                            <div className='col-md-5 text-right'>
                              <p className='services-title'>Cant. de Usos</p>
                            </div>
                          </div>
                        </div>
                        {this.getServiceRows(item).map((row) => (
                          <div className='service-item' key={row.key}>
                            <div className='row'>
                              <div className='col-md-10'>
                                <p className='service-name'>- {row.label}</p>
                              </div>
                              <div className='col-md-2 text-right'>
                                <p className='service-name'>
                                  {row.display === 'check' ? (
                                    <img src={CheckWhiteIcon} />
                                  ) : (
                                    row.value
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {this.props.current_plan_user?.membership_id == item.id ? (
                          <>
                            <div className='label-current-membership'>Membresía actual</div>
                            {this.props.current_plan_user?.plan_payment?.mode ==
                              Constants.PLAN_PAYMENT_MODES.MONTH && (
                              <button
                                className='btn btn-acquire btn-acquire-pay'
                                onClick={() => this.props.onClose?.call(this, false, true, item.id)}
                              >
                                Pagar
                              </button>
                            )}
                          </>
                        ) : (
                          <button
                            className='btn btn-acquire'
                            onClick={() => this.handleSelectMembership(item)}
                          >
                            Adquirir
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {this.state.packages.length > 0 && (
              <div className='container-memberships' ref={this.packagesRef}>
                <p className='title'>Paquetes</p>
                <p className='subtitle'>
                  Complementa tu membresía o adquiere el que más se ajuste a lo que necesitas
                </p>
                <div className='row'>
                  {this.state.packages.map((item: PackagePlan) => (
                    <div className='col-md-4' key={`package-${item.id}`}>
                      <div className='item-membership item-package'>
                        <div className='container-image'>
                          <div style={{ flex: 1 }}>
                            <p className='name'>{item.name}</p>
                            <div className='amount-row'>
                              <p className='amount'>{Globals.formatMiles(item.price)}</p>
                              <p className='amount-note'>Precio no incluye IVA</p>
                            </div>
                            {!!item.duration_value && (
                              <p className='package-duration'>
                                Vigencia: {item.duration_value}{' '}
                                {item.duration_unit === 'months' ? 'meses' : 'días'}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className='container-white'>
                          <div className='service-item'>
                            <div className='row'>
                              <div className='col-md-7'>
                                <p className='services-title'>Servicios contemplados</p>
                              </div>
                              <div className='col-md-5 text-right'>
                                <p className='services-title'>Cant. de Usos</p>
                              </div>
                            </div>
                          </div>
                          {this.getPackageServiceRows(item).map((row) => (
                            <div className='service-item' key={row.key}>
                              <div className='row'>
                                <div className='col-md-10'>
                                  <p className='service-name'>- {row.label}</p>
                                </div>
                                <div className='col-md-2 text-right'>
                                  <p className='service-name'>
                                    {row.display === 'check' ? (
                                      <img src={CheckWhiteIcon} />
                                    ) : (
                                      row.value
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                          <button
                            className='btn btn-acquire'
                            onClick={() => this.handleSelectPackage(item)}
                          >
                            Adquirir
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {step == STEPS.TIME && (
          <>
            <p className='title'>Mi pedido</p>
            <StepsContainer step={step} />
            <div className='container-mode'>
              <h3>{membership?.name}</h3>
              {(() => {
                const hasMonthly =
                  membership?.price_month != null && Number(membership.price_month) > 0;
                const hasYearly =
                  membership?.price_year != null && Number(membership.price_year) > 0;
                return (
                  <div className='container-total'>
                    {hasMonthly && (
                      <div className='row row-total'>
                        <div className='col-6'>
                          <div
                            className='container-circle'
                            onClick={() => this.setMode(MODES.MONTH)}
                          >
                            <div
                              className={`circle ${this.state.mode == MODES.MONTH ? 'active' : ''}`}
                            ></div>
                            <p>Mensual</p>
                          </div>
                        </div>
                        <div className='col-6 text-right'>
                          <p className='bold'>{Globals.formatMiles(membership?.price_month)}</p>
                        </div>
                      </div>
                    )}
                    {hasMonthly && hasYearly && <div className='separator' />}
                    {hasYearly && (
                      <div className='row row-total'>
                        <div className='col-6'>
                          <div
                            className='container-circle'
                            onClick={() => this.setMode(MODES.YEAR)}
                          >
                            <div
                              className={`circle ${this.state.mode == MODES.YEAR ? 'active' : ''}`}
                            ></div>
                            <p>Anual</p>
                          </div>
                        </div>
                        <div className='col-6 text-right'>
                          <p className='bold'>{Globals.formatMiles(membership?.price_year)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className='container-total' style={{ paddingBottom: '10px' }}>
              <div className='row row-total'>
                <div className='col-6'>
                  <p>Sub-total</p>
                </div>
                <div className='col-6 text-right'>
                  <p className='bold'>{Globals.formatMiles(subtotal)}</p>
                </div>
              </div>
              <div className='separator' />
              <div className='row row-total'>
                <div className='col-6'>
                  <p>Impuesto</p>
                </div>
                <div className='col-6 text-right'>
                  <p className='bold'>{Globals.formatMiles(iva_total)}</p>
                </div>
              </div>
              <div className='total-blue'>
                <div className='row'>
                  <div className='col-6'>
                    <p className='bold'>Total</p>
                  </div>
                  <div className='col-6 text-right'>
                    <p className='bold'>{Globals.formatMiles(total)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className='text-center'>
              <button
                className='btn btn-next'
                onClick={() => {
                  if (!this.state.mode) {
                    Globals.showError('Por favor, seleccione un modo de pago');
                    return;
                  }
                  this.setStep(STEPS.PAYMENT_METHOD);
                }}
              >
                Pagar
              </button>
            </div>
          </>
        )}

        {step == STEPS.PAYMENT_METHOD && (
          <>
            <p className='title'>Mi pedido</p>
            <StepsContainer step={step} />

            <div className='container-total' style={{ paddingBottom: '10px' }}>
              <div className='row row-total'>
                <div className='col-6'>
                  <p>Sub-total</p>
                </div>
                <div className='col-6 text-right'>
                  <p className='bold'>{Globals.formatMiles(subtotal + discount)}</p>
                </div>
              </div>
              {discount > 0 && (
                <>
                  <div className='separator' />
                  <div className='row row-total'>
                    <div className='col-6'>
                      <p>
                        Descuento {promoQuote?.promo?.code} (-{promoQuote?.promo?.discount_pct}%)
                      </p>
                    </div>
                    <div className='col-6 text-right'>
                      <p className='bold'>- {Globals.formatMiles(discount)}</p>
                    </div>
                  </div>
                </>
              )}
              <div className='separator' />
              <div className='row row-total'>
                <div className='col-6'>
                  <p>IVA</p>
                </div>
                <div className='col-6 text-right'>
                  <p className='bold'>{Globals.formatMiles(iva_total)}</p>
                </div>
              </div>
              {this.state.payment_method_id?.toString() ===
                Constants.PAYMENT_METHODS.STRIPE.toString() && (
                <>
                  <div className='separator' />
                  <div className='row row-total'>
                    <div className='col-6'>
                      <p>IGTF</p>
                    </div>
                    <div className='col-6 text-right'>
                      <p className='bold'>{Globals.formatMiles(igtf_total)}</p>
                    </div>
                  </div>
                </>
              )}
              <div className='total-blue' style={{ marginTop: '10px' }}>
                <div className='row'>
                  <div className='col-6'>
                    <p className='bold'>Total</p>
                  </div>
                  <div className='col-6 text-right'>
                    <p className='bold'>{Globals.formatMiles(total)}</p>
                  </div>
                </div>
              </div>
            </div>

            <p className='total'>{Globals.formatMiles(total)}</p>
            <p className='total-subtitle'>
              {this.state.selected_package
                ? this.state.selected_package?.name
                : `Pago ${this.state.mode == MODES.MONTH ? 'Mensual' : 'Anual'}`}
            </p>

            {/* Solo compra de membresía (no paquetes). Lo ve quien aún no pagó su primera
                membresía (código de referido) y, mientras el backend reporte una promoción
                vigente (`promo_active`), cualquiera que elija un plan anual -- ver
                canShowCodeField(). Un código de referido de alguien que ya compró lo sigue
                rechazando el backend al pagar (validateReferralCodeForPurchase). */}
            {this.canShowCodeField() && (
              <div className='container-referral-code'>
                <p className='referral-label'>
                  {this.props.user?.first_membership_paid_at
                    ? '¿Tienes un código promocional?'
                    : '¿Tienes un código de referido o promocional?'}
                </p>
                <div className='referral-row'>
                  <Input
                    color='white'
                    placeholder='Código'
                    name='referral_code'
                    value={this.state.form.referral_code}
                    onChange={this.changeReferralCode}
                    onKeyDown={(e: any) => e.key === 'Enter' && this.applyPromoCode()}
                    containerStyle={{ marginBottom: 0 }}
                  />
                  <button className='btn btn-apply-promo' onClick={this.applyPromoCode}>
                    Aplicar
                  </button>
                </div>
                {this.isOwnReferralCode() && (
                  <p className='referral-error'>No puedes usar tu propio código de referido</p>
                )}
                {promoQuote?.promo && (
                  <p className='promo-applied'>
                    Código {promoQuote.promo.code} aplicado: {promoQuote.promo.discount_pct}% de
                    descuento en tu plan anual
                  </p>
                )}
                {!promoQuote &&
                  this.state.quote?.key === this.getQuoteKey() &&
                  !this.state.quote?.data?.promo && (
                    <p className='promo-note'>
                      {this.props.user?.first_membership_paid_at
                        ? 'Este código no corresponde a una promoción vigente.'
                        : 'Este código no tiene descuento. Se validará como código de referido al pagar.'}
                    </p>
                  )}
              </div>
            )}

            <div className='row row-payment-methods'>
              {this.state.payment_methods.map((item: any) => (
                <div className='col-md-6' key={item.id}>
                  <button
                    className={`btn btn-payment-method ${item.only_icon ? 'center' : ''} ${item.id == payment_method_id ? 'active' : ''}`}
                    style={{ backgroundColor: item.color || Colors.blue_light }}
                    onClick={() => this.selectPaymentMethod(item.id)}
                  >
                    <img src={Constants.BASE_PUBLIC + item.icon} />
                    {!item.only_icon && <p className='method-name'>{item.name}</p>}
                  </button>
                </div>
              ))}
            </div>

            {/* Ancla de scroll — el formulario aparece aquí */}
            <div ref={this.formRef}>
              {showBsBreakdown && (
                <div className='container-total bs-breakdown' style={{ paddingBottom: '10px' }}>
                  <div className='row row-total'>
                    <div className='col-6'>
                      <p>Costo del paquete</p>
                    </div>
                    <div className='col-6 text-right'>
                      <p className='bold'>{Globals.formatMiles(subtotal_bs, true, 'Bs.')}</p>
                    </div>
                  </div>
                  <div className='separator' />
                  <div className='row row-total'>
                    <div className='col-6'>
                      <p>IVA ({Number(this.state.iva || 0)}%)</p>
                    </div>
                    <div className='col-6 text-right'>
                      <p className='bold'>{Globals.formatMiles(iva_bs, true, 'Bs.')}</p>
                    </div>
                  </div>
                  <div className='total-blue' style={{ marginTop: '10px' }}>
                    <div className='row'>
                      <div className='col-6'>
                        <p className='bold'>Total a pagar</p>
                      </div>
                      <div className='col-6 text-right'>
                        <p className='bold'>{Globals.formatMiles(total_bs, true, 'Bs.')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {payment_method_id == Constants.PAYMENT_METHODS.PAYMENT_MOBILE.toString() && (
                <div className='container-payment-mobile'>
                  <div className='row'>
                    <div className='col-md-12'>
                      <p className='bank'>
                        {bank_data?.bank?.name} - {bank_data?.bank?.code}
                      </p>
                    </div>
                    <div className='col-md-6'>
                      <p className='label-data'>Teléfono</p>
                    </div>
                    <div className='col-md-6 text-right'>
                      <div className='container-copy'>
                        <p className='bank-data'>
                          {bank_data?.phonecode?.name}
                          {bank_data?.phone}
                        </p>
                        <a
                          href='#'
                          onClick={(e: any) =>
                            this.clipboard(e, bank_data?.phonecode?.name + bank_data?.phone)
                          }
                        >
                          <img src={CopyIcon} />
                        </a>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <p className='label-data'>Cédula/RIF</p>
                    </div>
                    <div className='col-md-6 text-right'>
                      <div className='container-copy'>
                        <p className='bank-data'>
                          {bank_data?.document_type?.name}
                          {bank_data?.document}
                        </p>
                        <a
                          href='#'
                          onClick={(e: any) =>
                            this.clipboard(e, bank_data?.document_type?.name + bank_data?.document)
                          }
                        >
                          <img src={CopyIcon} />
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className='text-center'>
                    <a href='#' className='btn-copy-all' onClick={(e: any) => this.copyAll(e)}>
                      <img src={CopyIcon} />
                      Copiar todo
                    </a>
                  </div>
                  <p className='subtitle-data'>Indique datos para realizar operación</p>
                  <div className='row'>
                    <div className='col-md-12'>
                      <Select
                        label='Banco'
                        color='white'
                        placeholder='Banco de origen'
                        name='bank_id'
                        onChange={this.change}
                        value={this.state.form.bank_id}
                        options={this.state.banks.map((item: any) => ({
                          value: item.id,
                          label: item.name,
                        }))}
                      />
                    </div>
                    <div className='col-md-6'>
                      <div className='container-select'>
                        <label>Teléfono</label>
                        <div>
                          <Select
                            color='white'
                            name='phonecode_id'
                            onChange={this.change}
                            value={this.state.form.phonecode_id}
                            options={this.state.phonecodes.map((item: any) => ({
                              value: item.id,
                              label: item.name,
                            }))}
                          />
                          <Input
                            maxLength={7}
                            type='number'
                            color='white'
                            value={this.state.form.phone}
                            name='phone'
                            onChange={this.change}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <DatePicker
                        label='Fecha'
                        placeholder='Fecha'
                        maxDate={moment().toDate()}
                        onChange={(text: string) =>
                          this.change({ target: { value: text, name: 'date' } })
                        }
                        value={this.state.form.date}
                      />
                    </div>
                  </div>
                  <Checkbox
                    label='Declaro que los fondos utilizados son de origen lícito'
                    checked={this.state.form.is_checked}
                    onChange={() =>
                      this.setState({
                        form: {
                          ...this.state.form,
                          is_checked: !this.state.form.is_checked,
                        },
                      })
                    }
                    name='is_checked'
                  />
                  <div className='text-center'>
                    <button className='btn btn-next' onClick={() => this.submit()}>
                      Pagar
                    </button>
                  </div>
                </div>
              )}

              {payment_method_id == Constants.PAYMENT_METHODS.STRIPE.toString() && (
                <div className='container-stripe'>
                  <div className='text-center'>
                    <img src={StripeImage} />
                  </div>
                  <p className='subtitle-data'>Indique datos para realizar operación</p>
                  <div className='row'>
                    <div className='col-md-12'>
                      <Input
                        maxLength={16}
                        type='number'
                        color='white'
                        value={this.state.form.number}
                        name='number'
                        placeholder='Número de tarjeta'
                        onChange={this.change}
                      />
                    </div>
                    <div className='col-md-6'>
                      <Select
                        color='white'
                        placeholder='Mes'
                        name='expiration_month'
                        onChange={this.change}
                        value={this.state.form.expiration_month}
                        options={this.state.months.map((item: any) => ({
                          value: item.value,
                          label: item.name,
                        }))}
                      />
                    </div>
                    <div className='col-md-6'>
                      <Select
                        color='white'
                        placeholder='Año'
                        name='expiration_year'
                        onChange={this.change}
                        value={this.state.form.expiration_year}
                        options={this.state.years.map((item: any) => ({
                          value: item.value,
                          label: item.name,
                        }))}
                      />
                    </div>
                    <div className='col-md-12'>
                      <Input
                        maxLength={4}
                        type='password'
                        color='white'
                        value={this.state.form.cvv}
                        name='cvv'
                        placeholder='Código (CVC)'
                        onChange={this.change}
                      />
                    </div>
                  </div>
                  <Checkbox
                    label='Declaro que los fondos utilizados son de origen lícito'
                    checked={this.state.form.is_checked}
                    onChange={() =>
                      this.setState({
                        form: {
                          ...this.state.form,
                          is_checked: !this.state.form.is_checked,
                        },
                      })
                    }
                    name='is_checked'
                  />
                  <div className='text-center'>
                    <button className='btn btn-next' onClick={() => this.submit()}>
                      Pagar
                    </button>
                  </div>
                </div>
              )}

              {payment_method_id == Constants.PAYMENT_METHODS.SYPAGO.toString() && (
                <div className='container-payment-mobile'>
                  <p className='subtitle-data'>Indique datos para realizar operación</p>
                  <div className='row'>
                    <div className='col-md-12'>
                      <Select
                        color='white'
                        label='Banco'
                        name='sypago_bank_code'
                        onChange={this.change}
                        value={this.state.form.sypago_bank_code}
                        options={this.state.sypago_banks.map((item: any) => ({
                          value: item.code || item.Code,
                          label: item.name || item.Name,
                        }))}
                      />
                    </div>
                    <div className='col-md-6'>
                      <div className='container-select'>
                        <label>Teléfono</label>
                        <div>
                          <Select
                            color='white'
                            name='sypago_phonecode'
                            onChange={this.change}
                            value={this.state.form.sypago_phonecode}
                            options={this.state.phonecodes.map((item: any) => ({
                              value: item.name,
                              label: item.name,
                            }))}
                          />
                          <Input
                            maxLength={7}
                            type='number'
                            color='white'
                            value={this.state.form.sypago_phone}
                            name='sypago_phone'
                            onChange={this.change}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='container-select'>
                        <label>Cédula/RIF</label>
                        <div>
                          <Select
                            color='white'
                            name='sypago_document_type'
                            onChange={this.change}
                            value={this.state.form.sypago_document_type}
                            options={this.state.document_types.map((item: any) => ({
                              value: item.name,
                              label: item.name,
                            }))}
                          />
                          <Input
                            maxLength={9}
                            type='number'
                            color='white'
                            value={this.state.form.sypago_document}
                            name='sypago_document'
                            onChange={this.change}
                          />
                        </div>
                      </div>
                    </div>
                    <Checkbox
                      label='Declaro que los fondos utilizados son de origen lícito'
                      checked={this.state.form.is_checked}
                      onChange={() =>
                        this.setState({
                          form: {
                            ...this.state.form,
                            is_checked: !this.state.form.is_checked,
                          },
                        })
                      }
                      name='is_checked'
                    />
                    {this.state.otp_send && (
                      <div className='col-md-12'>
                        <p className='otp'>
                          Su banco se comunicará con usted para enviarle la clave OTP. Por favor,
                          ingrese su clave OTP:
                        </p>
                        <Input
                          maxLength={8}
                          type='number'
                          color='white'
                          value={this.state.form.sypago_otp}
                          name='sypago_otp'
                          placeholder='Clave OTP'
                          onChange={(e: any) => this.change(e, false)}
                        />
                      </div>
                    )}
                  </div>
                  <div className='text-center'>
                    <button className='btn btn-next' onClick={() => this.submit()}>
                      {this.state.otp_send ? 'Pagar' : 'Continuar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }
}

export default connect((state: RootState) => {
  return { user: state.user };
})(AddMemberships);

export { StepsContainer };

const StepsContainer = (params: any) => {
  return (
    <div className='text-center'>
      <div className='container-steps'>
        <div className={`step ${params.step >= STEPS.TIME ? 'active' : ''}`}>
          <p>Precio</p>
        </div>
        <div className='separator'></div>
        <div className={`step ${params.step >= STEPS.PAYMENT_METHOD ? 'active' : ''}`}>
          <p>Método de pago</p>
        </div>
      </div>
    </div>
  );
};
